<template>
  <v-navigation-drawer v-model="menuIsOpen" location="left" temporary width="280">
    <!-- Brand -->
    <div class="nav-brand">
      <v-img :src="require('@/assets/images/Logo.svg')" width="34" height="34" style="flex-shrink:0;" />
      <div class="nav-brand-text">
        <div class="nav-brand-title">My First Business</div>
        <div class="nav-brand-version">v{{ version }}</div>
      </div>
    </div>

    <!-- Nav items -->
    <div style="height: calc(100% - 128px); overflow-y: auto; overflow-x: hidden;" class="py-1">
      <template v-for="(category, catIndex) in navContent" :key="catIndex">
        <div class="nav-category-label">{{ category.label }}</div>
        <router-link
          v-for="item in category.items"
          :key="item.path"
          :to="item.path"
          style="text-decoration: none;"
          @click="menuIsOpen = false"
        >
          <div :class="['nav-item-container', $route.path === item.path ? 'nav-item-container--active' : '']" :style="$route.path === item.path ? { '--nav-item-color': getNavColorVar(item.color) } : {}">
            <v-list-item
              :active="$route.path === item.path"
              :color="item.color"
              density="comfortable"
            >
              <template v-slot:prepend>
                <v-icon :color="$route.path === item.path ? item.color : 'grey-darken-1'" size="20">{{ item.icon }}</v-icon>
              </template>
              <v-list-item-title>
                <span :class="['nav-item-label', $route.path !== item.path ? 'text-medium-emphasis' : 'text-' + item.color]">
                  {{ item.title }}
                </span>
              </v-list-item-title>
            </v-list-item>
          </div>
        </router-link>
      </template>
    </div>

    <!-- Logout -->
    <div style="border-top: 1px solid rgba(255,255,255,0.07); padding: 4px 0;">
      <a @click.prevent.stop="logout" style="text-decoration: none;">
        <div class="nav-item-container">
          <v-list-item density="comfortable">
            <template v-slot:prepend>
              <v-icon color="error" size="20">mdi-logout</v-icon>
            </template>
            <v-list-item-title>
              <span class="nav-item-label text-error">Déconnexion</span>
            </v-list-item-title>
          </v-list-item>
        </div>
      </a>
    </div>
  </v-navigation-drawer>
</template>

<script>
import navItems from "@/data/navItems.js"
import { getAuth } from '@firebase/auth'
import { useProfileStore } from '@/stores/profile'
import { useAppStore } from '@/stores/appdata'
import packageJSON from '@/../package.json'
import { rolesOf } from '@/services/roles'

const NAV_CATEGORY_LABELS = [
  'Principal', 'Communications', 'Mon Activité', 'Finance',
  'Équipes', 'Paramètres', 'Résultats', 'Templates', 'Dev'
]

export default {
    setup() {
      return {
        navItems,
        profileStore: useProfileStore(),
        appDataStore: useAppStore(),
      }
    },
    data() {
        return {
            unsub: [],
            menuIsOpen : false,
            version: packageJSON.version,
        }
    },
    computed: {
      navContent() {
        let nav = []
        
        if(!this.profileStore.profile || !this.appDataStore.appdata || !this.appDataStore.appdata.parameters || !this.appDataStore.appdata.parameters.roles || (!this.profileStore.team && this.profileStore.profile.role === 'User')) {
          return nav
        }

        let currentTeam = this.profileStore.team
        let currentRoles = []
        // Le rôle par défaut n'est pas inscrit dans l'équipe : il est ajouté à la lecture.
        for(let roleId of rolesOf(currentTeam, this.profileStore.profile.id, this.appDataStore.appdata.parameters.roles)){
          if(this.appDataStore.appdata.parameters.roles.find(role => role.name === roleId)) {
            currentRoles.push(this.appDataStore.appdata.parameters.roles.find(role => role.name === roleId))
          }
        }

        let displayParameters = {
          compagny: false,
          web: false,
          messageCenter : false,
          depot : false,
          buildings : false,
          tools : false,
          stuffs : false,
          staff : false,
          suppliers : false,
          trainings : false,
          offers : false,
          contracts : false,
          communications : false,
          bank : false,
          invoices : false,
          finance : false,
          data : false
        }

        if (this.profileStore.profile && this.profileStore.profile.role == 'User') {
          for (const key in displayParameters) {
            displayParameters[key] = false
            for (const role of currentRoles) {
              if (role.accessPages && role.accessPages[key]) {
                displayParameters[key] = true
              }
            }
          }
        }

        navItems.forEach((cat, catIdx) => {
          let items = []
          
          cat.forEach(item => {
            let itemRoute = this.$router.resolve({ path: item.path })
            
            let visible = true
            if(itemRoute.meta.accessRules.accountNeeded && !this.profileStore.profile) {
              visible = false
            }
            if(itemRoute.meta.accessRules.devNeeded) {
              visible = false
            }
            if(itemRoute.meta.accessRules.accountNeeded && !itemRoute.meta.accessRules.roleNeeded.includes('User')){
              visible = false
            }

            if(itemRoute.meta.accessRules.displayParameter && !displayParameters[itemRoute.meta.accessRules.displayParameter]){
              visible = false
            }

            if(this.profileStore.profile.role != 'User'){
              visible = true
            }

            if(visible) {
              item.title = itemRoute.meta.title
              item.name = itemRoute.name
              items.push(item)
            }
          })

          if (items.length > 0) {
            nav.push({ label: NAV_CATEGORY_LABELS[catIdx] || '', items })
          }
        })
        
        let currentRoute = this.$router.currentRoute.value
        let needRedirection = false
        if(currentRoute.meta.accessRules.accountNeeded && !this.profileStore.profile) {
          needRedirection = true
        }
        if(currentRoute.meta.accessRules.devNeeded && !this.profileStore.profile.dev) {
          needRedirection = true
        }
        if(currentRoute.meta.accessRules.accountNeeded && !currentRoute.meta.accessRules.roleNeeded.includes('User') && this.profileStore.profile.role == 'User'){
          needRedirection = true
        }
        if(this.profileStore.profile.role == 'User' && currentRoute.meta.accessRules.displayParameter && !displayParameters[currentRoute.meta.accessRules.displayParameter]){
          needRedirection = true
        }
        if(this.profileStore.profile && this.profileStore.profile.role != 'User'){
          needRedirection = false
        }
        
        if(needRedirection) {
          this.$router.push('/home')
        }

        return nav
      },
      old_navContent() {
        let nav = []
        
        if(!this.store.profile) {
          return nav
        }

        navItems.forEach(cat => {
          let userItems = []
          let adminItems = []

          cat.forEach(item => {
            let itemRoute = this.$router.resolve({ path: item.path })
            
            let visible = true
            if(itemRoute.meta.accessRules.accountNeeded && !this.store.profile) {
              visible = false
            }
            if(itemRoute.meta.accessRules.devNeeded) {
              visible = false
            }
            if(itemRoute.meta.accessRules.accountNeeded && !itemRoute.meta.accessRules.roleNeeded.includes('User')){
              visible = false
            }

            if(visible) {
              item.title = itemRoute.meta.title
              item.name = itemRoute.name
              userItems.push(item)
            }
          })

          cat.forEach(item => {
            let itemRoute = this.$router.resolve({ path: item.path })
            
            let visible = true
            if(!this.store.profile || this.store.profile.role == 'User') {
              visible = false
            }
            if(itemRoute.meta.accessRules.devNeeded && !this.store.profile.dev) {
              visible = false
            }
            if(itemRoute.meta.accessRules.accountNeeded && itemRoute.meta.accessRules.roleNeeded.includes('User')){
              visible = false
            }

            if(visible) {
              item.dev = itemRoute.meta.accessRules.devNeeded
              item.adminOnly = true

              if(itemRoute.meta.accessRules.roleNeeded.includes('Admin')){
                item.superAdminOnly = false
              }else if(itemRoute.meta.accessRules.roleNeeded.includes('SuperAdmin')){
                item.superAdminOnly = true
              }

              item.name = itemRoute.name
              item.title = itemRoute.meta.title
              
              adminItems.push(item)
            }
          })
          if (userItems.length > 0) {
            nav.push(userItems)
          }
          if (adminItems.length > 0) {
            nav.push(adminItems)
          }
        })
        return nav
      }
    },
    methods: {
      toggleMenu(){
        this.menuIsOpen = !this.menuIsOpen
      },
      logout(){
        getAuth().signOut()
        this.$router.push('/login')
      },
      getNavColorVar(colorName) {
        const themeColors = ['primary', 'secondary', 'accent', 'error', 'info', 'success', 'warning']
        if (themeColors.includes(colorName)) {
          return `var(--v-theme-${colorName})`
        }
        const palette = {
          'yellow': '255, 235, 59',
          'deep-orange': '255, 87, 34',
        }
        return palette[colorName] || 'var(--v-theme-primary)'
      },
    },
}

</script>